/**
 * Panel del Promotor Digital — orquestador.
 *
 * Boot: canjear ?acceso= → login → panel con 4 vistas (read-only):
 *   Compartir · Afiliados · Réditos · (onboarding al primer login)
 *
 * Vanilla JS, mobile-first. NO mueve dinero — el backend (caja 'enlace')
 * bloquea cualquier operación; este front solo comparte el link y muestra
 * el relatorio.
 */

import '@shared/components/modal-formulario/qrcode-lib.js'; // registra window.QRCode (lo usa compartir.js)
import { KEYS } from '@shared/config/api-nomenclatura.js';
import PromoterConfig from './config.js';
import ApiClient from './api-client.js';
import i18n, { t } from './languages.js';
import { renderLogin } from './views/login.js';
import { renderActivacion } from './views/activacion.js';
import { renderCambiarContrasena } from './views/cambiar-contrasena.js';
import { renderCambiarCorreo } from './views/cambiar-correo.js';
import { renderRecuperarContrasena } from './views/recuperar-contrasena.js';
import { renderOnboarding } from './views/onboarding.js';
import { renderCajaCodigo } from './views/caja-codigo.js';
import { esStandalone, caminoDeInstalacion } from './systems/deteccion-entorno.js';
import { renderCompartir } from './views/compartir.js';
import { renderAfiliados } from './views/afiliados.js';
import { renderReditos } from './views/reditos.js';
import { renderEmbudo } from './views/embudo.js';
import { renderTags } from './views/tags.js';
import { renderCampanas } from './views/campanas.js';
import { renderRanking } from './views/ranking.js';
import { initInstallPrompt, renderBotonInstalar, manejarClickInstalar, mostrarGuiaInstalacion } from './pwa-install.js';
import { registrarServiceWorker } from './push-notificaciones.js';
import { escapeHtml } from './systems/escape-html.js';

const app = document.getElementById('promoter-app');

// Config de la sesión (urlJuego, nodo, operaciones) — viene de GET /api/cashier/config.
let _config = null;
let _vistaActual = 'compartir';

// ── Boot ─────────────────────────────────────────────────────────────────
async function boot() {
    initInstallPrompt();
    registrarServiceWorker(); // SW para PWA + Web Push (no pide permiso aún)
    // Sesión caída: si el admin REGENERÓ el link (device_revoked) la app limpió el
    // device-token → pantalla "pedí un enlace nuevo"; si fue expiración normal →
    // login con el dispositivo conservado.
    ApiClient.setOnSessionExpired(motivo => {
        mostrarLogin(motivo === 'device_revoked' ? t('login.enlaceRevocado') : t('login.sesionExpiro'));
    });

    if (ApiClient.isLoggedIn()) {
        await entrarAlPanel();
        return engancharRevalidacionAlVolverAFoco();
    }
    if (ApiClient.hasDispositivoToken()) {
        // Ya anclado (canjeó antes): si la cuenta nació sin contraseña, derivar a
        // activación; si no, login. (Si la consulta de red falla, login; desde ahí
        // el 409 CUENTA_SIN_CONTRASENA re-deriva.)
        await mostrarActivacionOLogin();
        return engancharRevalidacionAlVolverAFoco();
    }

    // Sin ancla. ¿Hay un código válido en la URL y NO estamos instalados?
    // → BIENVENIDA PRIMERO, sin canjear todavía. CLAVE del flujo: el código NO se
    // canjea en el navegador hasta que el promotor elige. Si elige instalar, el
    // código viaja vivo a la app instalada (en iOS, canjearlo acá lo quemaría y la
    // app instalada quedaría sin forma de entrar). Si elige web, ahí se canjea.
    const tokenEnUrl = ApiClient.leerTokenValidoDeLaUrl();
    if (tokenEnUrl && !esStandalone()) {
        mostrarBienvenida(tokenEnUrl);
        return engancharRevalidacionAlVolverAFoco();
    }

    // App instalada sin ancla: en iOS el sandbox no comparte storage con Safari y
    // arrancó en "/" sin el código → si la URL trae código lo canjeamos virgen; si
    // no, la caja para que el promotor pegue su código (lo tiene en su WhatsApp).
    if (esStandalone()) {
        if (tokenEnUrl) {
            const ok = await ApiClient.canjearTokenAcceso(tokenEnUrl, { limpiarUrl: true });
            if (ok) {
                await mostrarActivacionOLogin();
                return engancharRevalidacionAlVolverAFoco();
            }
        }
        mostrarCajaCodigo();
        return engancharRevalidacionAlVolverAFoco();
    }

    // Navegador sin ancla y sin código en la URL → flujo normal (login).
    mostrarLogin();
    engancharRevalidacionAlVolverAFoco();
}

// Tras tener device-token: activación si la cuenta nació sin contraseña, login si no.
async function mostrarActivacionOLogin() {
    const estado = await ApiClient.activacionEstado();
    if (estado && estado.ok && estado.requiereActivacion) {
        mostrarActivacion(estado);
    } else {
        mostrarLogin();
    }
}

function engancharRevalidacionAlVolverAFoco() {
    // Revalidar al volver a FOCO: si el enlace ya no es válido (admin lo regeneró
    // mientras la app estaba en segundo plano), un ping ligero devuelve 401 y el
    // handler global expulsa. Si es error de RED, NO expulsa (get devuelve
    // NETWORK_ERROR, no SESSION_EXPIRED — distinguimos auth de red).
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && ApiClient.isLoggedIn()) {
            ApiClient.get('/api/cashier/config'); // el 401 lo maneja _manejarRechazoAuth
        }
    });
}

function mostrarLogin(mensajeError) {
    app.innerHTML = '';
    app.appendChild(
        renderLogin({
            tieneDispositivo: ApiClient.hasDispositivoToken(),
            mensajeError,
            emailInicial: ApiClient.getUltimoEmail(),
            onLogin: async (email, password) => {
                const r = await ApiClient.login(email, password);
                if (!r.ok) {
                    // La cuenta existe pero nació sin contraseña → derivar al flujo
                    // de activación (no es un error de credenciales).
                    if (r.code === KEYS.ERROR_CODES.CUENTA_SIN_CONTRASENA) {
                        const estado = await ApiClient.activacionEstado();
                        if (estado && estado.ok && estado.requiereActivacion) {
                            mostrarActivacion(estado);
                            return null;
                        }
                    }
                    return r.error || 'No se pudo ingresar';
                }
                await entrarAlPanel();
                return null;
            },
            onOlvido: () => mostrarRecuperarContrasena(),
        })
    );
}

function mostrarRecuperarContrasena() {
    app.innerHTML = '';
    app.appendChild(
        renderRecuperarContrasena({
            onRestablecida: () => mostrarLogin(t('recuperar.listo')),
            onVolver: () => mostrarLogin(),
        })
    );
}

function mostrarActivacion(estado) {
    app.innerHTML = '';
    app.appendChild(
        renderActivacion({
            estado,
            onActivada: () => {
                // El promotor acaba de definir correo+contraseña. El backend ya dejó
                // la sesión iniciada → entra DIRECTO al panel, sin re-teclear nada.
                // Si por algún motivo no quedó logueado (fallback), va al login con
                // el correo ya prefilled (solo teclea la seña que acaba de crear).
                if (ApiClient.isLoggedIn()) {
                    entrarAlPanel();
                } else {
                    mostrarLogin(t('activacion.listo'));
                }
            },
        })
    );
}

async function entrarAlPanel() {
    // Cargar config de la sesión (urlJuego para el link, nodo, etc.).
    const config = await ApiClient.get('/api/cashier/config');
    if (!config || config.ok === false) {
        // Si el fallo fue de AUTH (401: enlace revocado / sesión expirada), el handler
        // global _manejarRechazoAuth YA mostró la pantalla con el mensaje correcto
        // (enlaceRevocado o sesionExpiro) — NO re-mostrar el login genérico encima,
        // que sobreescribiría ese mensaje. Solo mostramos el genérico para errores reales.
        if (config && config.error === 'SESSION_EXPIRED') return;
        mostrarLogin(t('login.error'));
        return;
    }
    _config = config;

    // Idioma del nodo (herencia fractal, viene en config). Setea el idioma de
    // toda la UI antes de renderizar. Cae a 'es' si no vino o no está soportado.
    const idiomaNodo = (config.nodo && config.nodo.idioma) || config.idioma;
    if (idiomaNodo) i18n.setLanguage(idiomaNodo);

    // La bienvenida épica ya se mostró en el boot (pre-canje, ver mostrarBienvenida).
    // Acá entramos directo al panel.
    _vistaActual = 'compartir';
    renderPanel();
}

// BIENVENIDA ÉPICA — primera pantalla al tocar el link (ANTES de canjear el código).
// El promotor elige: INSTALAR (no canjea acá; el código viaja vivo a la app instalada)
// o SEGUIR EN WEB (recién ahí se canjea y sigue a activación/login). `token` es el
// código válido leído de la URL, que pasamos al canje cuando elige web.
function mostrarBienvenida(token) {
    app.innerHTML = '';
    app.appendChild(
        renderOnboarding({
            codigo: token, // para la cajita copiable de la guía iOS
            onSeguirWeb: async () => {
                // Web: canjear el código ahora y seguir el flujo de anclaje.
                const ok = await ApiClient.canjearTokenAcceso(token, { limpiarUrl: true });
                if (ok) {
                    await mostrarActivacionOLogin();
                } else {
                    // Código ya usado/vencido o sin red → login (desde ahí puede
                    // reintentar o el operador regenera el link).
                    mostrarLogin(t('login.error'));
                }
            },
        })
    );
}

// App instalada sin ancla → caja para escribir/pegar el código (la tiene en su
// WhatsApp). Al conectar, re-evaluar el boot (puede requerir activación o ir al panel).
function mostrarCajaCodigo() {
    app.innerHTML = '';
    app.appendChild(
        renderCajaCodigo({
            onConectado: async () => {
                // Ya hay dispositivoToken: seguir el mismo árbol que el boot normal.
                const estado = await ApiClient.activacionEstado();
                if (estado && estado.ok && estado.requiereActivacion) {
                    mostrarActivacion(estado);
                } else {
                    mostrarLogin();
                }
            },
        })
    );
}

// ── Panel con nav inferior ───────────────────────────────────────────────
// Los labels se resuelven con t() en cada render (para reflejar el idioma activo).
// Material Design: máx 5 en bottom nav. Las 4 vistas de uso frecuente van en la
// barra; las 3 ocasionales (analítica/config) al sheet "Más".
const NAV = [
    { id: 'compartir', key: 'nav.compartir', icon: iconShare },
    { id: 'reditos', key: 'nav.reditos', icon: iconMoney },
    { id: 'afiliados', key: 'nav.afiliados', icon: iconUsers },
    { id: 'ranking', key: 'nav.ranking', icon: iconTrophy },
    { id: 'mas', key: 'nav.mas', icon: iconMore },
];
const NAV_SECUNDARIAS = [
    { id: 'embudo', key: 'nav.embudo', icon: iconFunnel },
    { id: 'campanas', key: 'nav.campanas', icon: iconChart },
    { id: 'tags', key: 'nav.tags', icon: iconTag },
    { id: 'cambiarpass', key: 'nav.cambiarPass', icon: iconLock },
    { id: 'cambiarcorreo', key: 'nav.cambiarCorreo', icon: iconMail },
];

function renderPanel() {
    const nombrePromotor = (ApiClient.getUser() && ApiClient.getUser().nombre) || 'Promotor';
    app.innerHTML = '';

    const wrap = document.createElement('div');
    wrap.style.cssText = 'flex:1; display:flex; flex-direction:column; min-height:100vh;';

    // Header
    const header = document.createElement('div');
    header.className = 'promoter-header';
    header.innerHTML = `
        <div>
            <h1>${escapeHtml(nombrePromotor)}</h1>
            <div class="sub">${t('login.titulo')}<span class="pm-build-badge">v${__BUILD_DATE__} · ${__BUILD_ID__}</span></div>
        </div>
        ${renderBotonInstalar()}
    `;
    const btnInstall = header.querySelector('#pm-install');
    if (btnInstall) {
        btnInstall.addEventListener('click', async () => {
            // En iOS (y cualquier entorno sin prompt nativo) manejarClickInstalar
            // devuelve false → mostramos la guía contextual (Compartir → Agregar a
            // inicio, o "abrí en Chrome/Safari"). Sin esto el botón quedaría inerte.
            const lanzoPromptNativo = await manejarClickInstalar();
            if (!lanzoPromptNativo) mostrarGuiaInstalacion(caminoDeInstalacion());
        });
    }
    wrap.appendChild(header);

    // Surface (la vista activa)
    const surface = document.createElement('div');
    surface.className = 'promoter-surface';
    wrap.appendChild(surface);

    // Nav inferior (4 + Más). "Más" se pinta activo si la vista actual es secundaria.
    const enSecundaria = NAV_SECUNDARIAS.some(s => s.id === _vistaActual);
    const nav = document.createElement('nav');
    nav.className = 'promoter-nav';
    nav.innerHTML = NAV.map(n => {
        const activo = n.id === 'mas' ? enSecundaria : n.id === _vistaActual;
        return `<button data-vista="${n.id}" class="${activo ? 'active' : ''}">${n.icon()}<span>${t(n.key)}</span></button>`;
    }).join('');
    nav.addEventListener('click', e => {
        const btn = e.target.closest('[data-vista]');
        if (!btn) return;
        if (btn.dataset.vista === 'mas') {
            abrirSheetMas();
            return;
        }
        _vistaActual = btn.dataset.vista;
        renderPanel();
    });
    wrap.appendChild(nav);

    app.appendChild(wrap);
    renderVistaEn(surface);
}

function renderVistaEn(surface) {
    surface.innerHTML = '';
    if (_vistaActual === 'compartir') {
        surface.appendChild(renderCompartir({ config: _config, user: ApiClient.getUser() }));
    } else if (_vistaActual === 'afiliados') {
        surface.appendChild(renderAfiliados({ apiGet: ApiClient.get, apiPost: ApiClient.post }));
    } else if (_vistaActual === 'embudo') {
        surface.appendChild(renderEmbudo({ apiGet: ApiClient.get }));
    } else if (_vistaActual === 'reditos') {
        surface.appendChild(renderReditos({ apiGet: ApiClient.get }));
    } else if (_vistaActual === 'tags') {
        surface.appendChild(renderTags({ apiGet: ApiClient.get, apiPost: ApiClient.post }));
    } else if (_vistaActual === 'campanas') {
        surface.appendChild(renderCampanas({ apiGet: ApiClient.get }));
    } else if (_vistaActual === 'ranking') {
        surface.appendChild(renderRanking({ apiGet: ApiClient.get }));
    } else if (_vistaActual === 'cambiarpass') {
        surface.appendChild(renderCambiarContrasena());
    } else if (_vistaActual === 'cambiarcorreo') {
        surface.appendChild(renderCambiarCorreo());
    }
}

// Sheet "Más": overlay con las vistas secundarias (analítica/config). Al elegir
// una, se cierra y se navega. Tocar el fondo o cerrar descarta sin navegar.
function abrirSheetMas() {
    const overlay = document.createElement('div');
    overlay.className = 'pm-sheet-overlay';
    overlay.innerHTML = `
        <div class="pm-sheet" role="dialog" aria-modal="true">
            <div class="pm-sheet-handle"></div>
            ${NAV_SECUNDARIAS.map(
                s => `<button class="pm-sheet-item" data-vista="${s.id}">
                    <span class="pm-sheet-icon">${s.icon()}</span>
                    <span>${t(s.key)}</span>
                </button>`
            ).join('')}
        </div>`;

    function cerrar() {
        overlay.classList.remove('open');
        setTimeout(() => overlay.remove(), 200);
    }
    overlay.addEventListener('click', e => {
        const item = e.target.closest('[data-vista]');
        if (item) {
            _vistaActual = item.dataset.vista;
            cerrar();
            renderPanel();
        } else if (e.target === overlay) {
            cerrar();
        }
    });
    document.body.appendChild(overlay);
    // Forzar reflow antes de la clase .open para que la transición de entrada corra.
    requestAnimationFrame(() => overlay.classList.add('open'));
}

// ── Íconos inline (sin dependencia) ──────────────────────────────────────
function iconShare() {
    return svg(
        '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" x2="12" y1="2" y2="15"/>'
    );
}
function iconUsers() {
    return svg(
        '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'
    );
}
function iconMoney() {
    return svg('<line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>');
}
function iconFunnel() {
    return svg(
        '<path d="M10 21v-7.6a1 1 0 0 0-.3-.7L3 6.2a1 1 0 0 1-.3-.7V4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v1.5a1 1 0 0 1-.3.7l-6.7 6.5a1 1 0 0 0-.3.7V18z"/>'
    );
}
function iconTag() {
    return svg(
        '<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/>'
    );
}
function iconLock() {
    return svg('<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>');
}
function iconMail() {
    return svg(
        '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>'
    );
}
function iconChart() {
    return svg(
        '<line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/>'
    );
}
function iconTrophy() {
    return svg(
        '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>'
    );
}
function iconMore() {
    return svg('<circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>');
}
function svg(inner) {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
}

boot();
